/** Pure route parsing (no DOM access, so it is unit-testable). */
export const SETTINGS_SECTIONS = [
  "general",
  "providers",
  "usage",
  "machines",
  "projects",
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/**
 * The active development context is a PROJECT:  /m/:machineId/p/:projectId[?chat=:chatId]
 * (M2C.1; before it was /m/:machineId/w/:id, where "w" was a workspace that is now called a Project).
 */
export type Route =
  | { name: "home" }
  | { name: "pair" }
  | { name: "invite" }
  | { name: "settings"; section: SettingsSection }
  | {
      name: "project";
      machineId: string;
      projectId?: string;
      chatId?: string;
      /** True when the URL used the retired /w/ form: the app redirects to the canonical /p/ route. */
      legacy?: true;
    };

const UUID = /^[0-9a-f-]{36}$/i;
const ID = /^[A-Za-z0-9_-]{1,64}$/;

export function parseRoute(pathname: string, search = ""): Route {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "pair") return { name: "pair" };
  if (parts[0] === "invite") return { name: "invite" };
  if (parts[0] === "settings") {
    const s = parts[1];
    if (s === "workspaces") return { name: "settings", section: "projects" };
    return {
      name: "settings",
      section: s && (SETTINGS_SECTIONS as readonly string[]).includes(s)
        ? (s as SettingsSection)
        : "general",
    };
  }
  if (parts[0] === "m" && parts[1] && UUID.test(parts[1])) {
    const legacy = parts[2] === "w";
    const projectId = (parts[2] === "p" || legacy) && parts[3] && ID.test(parts[3]) ? parts[3] : undefined;
    const chat = new URLSearchParams(search).get("chat");
    const chatId = projectId && chat && UUID.test(chat) ? chat : undefined;
    return {
      name: "project",
      machineId: parts[1],
      ...(projectId ? { projectId } : {}),
      ...(chatId ? { chatId } : {}),
      ...(legacy && projectId ? { legacy: true as const } : {}),
    };
  }
  return { name: "home" };
}

export const projectPath = (machineId: string, projectId: string, chatId?: string) =>
  `/m/${machineId}/p/${projectId}${chatId ? `?chat=${chatId}` : ""}`;

/**
 * Canonical URL for a retired route, or null when the URL is already canonical. Legacy ids need no translation: the
 * migration kept every old workspace id as the id of the corresponding Project.
 */
export function canonicalProjectUrl(pathname: string, search = ""): string | null {
  const r = parseRoute(pathname, search);
  if (r.name !== "project" || !r.legacy || !r.projectId) return null;
  return projectPath(r.machineId, r.projectId, r.chatId);
}
